/*
 * SP: two-row category slider (sections/collection-list.liquid, setting
 * sp_two_row_slider). The track is a native scroll-snap scroller, so touch
 * swiping needs no script at all. This file drives the desktop arrows (one
 * click scrolls one visible page, each arrow disables at its end), the
 * "1 | 2" page count between them (the standard pager under the track,
 * 2026-10-04; it replaced a phone-only progress rail), Left/Right keys on the
 * focused track, and mouse/pen drag with inertia (below).
 *
 * Motion (2026-10-03): scroll-driven state is written once per frame (rAF).
 * Arrow scrolls use the browser's own smooth scroll, which cooperates
 * with scroll-snap; a JS tween of scrollLeft would fight the snap. With
 * prefers-reduced-motion the arrows jump instead of gliding.
 */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(section) {
    var track = section.querySelector('[data-sp-cat-track]');
    var prev = section.querySelector('[data-sp-cat-prev]');
    var next = section.querySelector('[data-sp-cat-next]');
    var current = section.querySelector('[data-sp-cat-current]');
    var total = section.querySelector('[data-sp-cat-total]');
    if (!track || !prev || !next || track.dataset.spCatReady) return;
    track.dataset.spCatReady = '1';

    function step() {
      var tile = track.firstElementChild;
      if (!tile) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      // computed width: unrounded (offsetWidth rounds and miscounts pages) and
      // untransformed (an .is-offpage tile is scaled down)
      var perTile = parseFloat(getComputedStyle(tile).width) + gap;
      // Whole tiles only, so every page lands on a snap point.
      return Math.max(1, Math.floor((track.clientWidth + gap) / perTile)) * perTile;
    }

    function go(dir) {
      track.scrollBy({ left: dir * step(), behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    var queued = false;
    function sync() {
      queued = false;
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
      section.classList.toggle('sp-cat-slider--static', max <= 1);
      if (current && total && max > 1) {
        // pages = one per arrow click: the first view, then one per step()
        // until the end (the last page may be a partial one, as the arrow
        // lands it). The end of the row always reads as the last page.
        var per = step();
        var pages = 1 + Math.ceil((max - 1) / per);
        var page = track.scrollLeft >= max - 1 ? pages : Math.min(pages, 1 + Math.round(track.scrollLeft / per));
        total.textContent = pages;
        current.textContent = page;
      }
    }
    function queue() {
      if (!queued) { queued = true; requestAnimationFrame(sync); }
    }

    var drag = null, swallowClick = false;
    function tileWidth() {
      var tile = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return tile ? parseFloat(getComputedStyle(tile).width) + gap : track.clientWidth;
    }
    function settle(target) {
      var per = tileWidth(), max = track.scrollWidth - track.clientWidth;
      var to = Math.max(0, Math.min(max, Math.round(target / per) * per));
      track.scrollTo({ left: to, behavior: reduce.matches ? 'auto' : 'smooth' });
      var done = function () { track.classList.remove('is-dragging'); track.removeEventListener('scrollend', done); };
      track.addEventListener('scrollend', done);
      setTimeout(done, 700); // browsers without scrollend
    }
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      if (track.scrollWidth - track.clientWidth <= 1) return;
      drag = { id: e.pointerId, x: e.clientX, left: track.scrollLeft, moved: false, t: e.timeStamp, vx: 0 };
    });
    track.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        drag.moved = true;
        track.setPointerCapture(e.pointerId);
        track.classList.add('is-dragging');
      }
      var dt = Math.max(1, e.timeStamp - drag.t);
      drag.vx = 0.8 * ((drag.lastX === undefined ? 0 : drag.lastX - e.clientX) / dt) + 0.2 * drag.vx;
      drag.lastX = e.clientX; drag.t = e.timeStamp;
      track.scrollLeft = drag.left - dx;
    });
    function end(e) {
      if (!drag || e.pointerId !== drag.id) return;
      var d = drag; drag = null;
      if (!d.moved) return;
      swallowClick = true;
      setTimeout(function () { swallowClick = false; }, 0); // only the click this release produces
      // inertia: carry the release velocity ~250ms forward, then snap to a tile
      // held still before letting go = no fling, the way a native swipe behaves
      var v = e.timeStamp - d.t > 100 ? 0 : d.vx;
      settle(track.scrollLeft + v * 250);
    }
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    // a drag that ends over a tile must not also open it
    track.addEventListener('click', function (e) {
      if (swallowClick) { e.preventDefault(); e.stopPropagation(); swallowClick = false; }
    }, true);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); }); // native image/link ghost drag

    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    track.addEventListener('keydown', function (e) {
      if (e.target !== track) return; // a focused tile link keeps its own keys
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    });
    if ('IntersectionObserver' in window && !reduce.matches) {
      // loss on animation"): the observer already hands over each tile's box and
      // the track's, so the stagger index comes from those. Reading offsetLeft or
      // a computed width here forced a style/layout pass mid-scroll and cost
      // 1-4 frames per flip; the tile pitch is cached and refreshed on resize.
      var pitch = tileWidth();
      window.addEventListener('resize', function () { pitch = tileWidth(); });
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var t = en.target;
          if (en.intersectionRatio >= 0.5) {
            var root = en.rootBounds, box = en.boundingClientRect;
            var col = root ? Math.max(0, Math.round((box.left - root.left) / pitch)) : 0;
            var row = root && box.top - root.top > box.height / 2 ? 1 : 0;
            t.style.setProperty('--sp-cat-i', Math.min(col + row, 6));
            t.classList.remove('is-offpage');
          } else if (!t.classList.contains('is-offpage')) {
            t.classList.add('is-offpage');
          }
        });
      }, { root: track, threshold: [0, 0.5] });
      Array.prototype.forEach.call(track.children, function (t) { io.observe(t); });

      // Layers only while it moves: .is-live gives each tile will-change from the
      // first sign of intent (pointer at the pager, a touch or press on the track,
      // a key, a wheel) until 900ms after the scroll settles. Promoted at rest the
      // 2nd and 4th columns rasterised at a fractional offset (softer text);
      // never promoted, desktop dropped more frames mid-flip.
      var idle = null;
      var live = function () {
        track.classList.add('is-live');
        clearTimeout(idle);
        idle = setTimeout(function () { track.classList.remove('is-live'); }, 900);
      };
      var pager = section.querySelector('.sp-cat-slider__pager');
      if (pager) pager.addEventListener('pointerenter', live);
      ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll', 'focusin'].forEach(function (ev) {
        track.addEventListener(ev, live, { passive: true });
      });
      if (pager) pager.addEventListener('focusin', live);
    }

    track.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    sync();
  }

  function run() {
    document.querySelectorAll('.collection-slider-row').forEach(function (row) {
      if (row.querySelector('.sp-cat-slider')) init(row);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
